import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../api/client'
import type { Role, TenantUser } from '../api/types'
import { usersApi } from '../api/users'
import { useAuth } from '../auth/useAuth'
import { Alert } from '../components/Alert'
import { Modal } from '../components/Modal'
import { StatusPill } from '../components/StatusPill'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import { useAsync } from '../hooks/useAsync'
import { formatInt, formatRelative, initials } from '../lib/format'

const ROLES: Role[] = ['Admin', 'User', 'ImportOnly', 'ExportOnly', 'DeleteOnly']

const ROLE_HELP: Record<string, string> = {
  Admin: 'Everything, including billing, companies and users.',
  User: 'Can run imports, exports and deletes. No admin screens.',
  ImportOnly: 'Can only run imports.',
  ExportOnly: 'Can only run exports.',
  DeleteOnly: 'Can only run deletes.',
}

const STATUS_TONE = { Active: 'ok', Invited: 'warn', Disabled: 'idle' } as const

/** `embedded` drops the page chrome (breadcrumb, title, `.wrap` padding) so the
 *  Account hub can host this screen inside a tab panel. Everything else — hooks,
 *  API calls, table, actions — is identical either way. */
export function UsersPage({ embedded = false }: { embedded?: boolean }) {
  const { user } = useAuth()
  const users = useAsync<TenantUser[]>(() => usersApi.list(), [])
  const [inviteOpen, setInviteOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<Role>('User')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const invite = async () => {
    setBusy(true)
    setError(null)
    try {
      await usersApi.invite(email.trim(), role, name.trim() || undefined)
      setInviteOpen(false)
      setEmail('')
      setName('')
      users.reload()
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : 'Could not send the invite.')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: string) => {
    setError(null)
    try {
      await usersApi.remove(id)
      users.reload()
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : 'Could not remove that user.')
    }
  }

  const rows = users.data ?? []

  const body = (
    <>
      <div className="phead">
        {embedded ? null : (
          <div>
            <div className="crumb">
              <Link to="/app">Home</Link> / Users &amp; roles
            </div>
            <h2>Users &amp; roles</h2>
          </div>
        )}
        <span className="spacer" />
        <Link className="btn btn-o" to="/app/companies">
          Manage companies
        </Link>
        <button className="btn btn-p" type="button" onClick={() => setInviteOpen(true)}>
          + Invite a user
        </button>
      </div>

      {error ? (
        <Alert tone="err" title="That did not work" style={{ marginBottom: 18 }}>
          {error}
        </Alert>
      ) : null}

      <div className="card">
        <div className="card-h">
          <h3>Users</h3>
          <span className="spacer" />
          <span className="fs12 faint">{formatInt(rows.length)} seats used</span>
        </div>

        {users.error ? (
          <ErrorState error={users.error} onRetry={users.reload} />
        ) : users.loading && !users.data ? (
          <LoadingState label="Loading your team…" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon="◎"
            title="It is just you here"
            body="Invite a colleague and pick the narrowest role that lets them do their job."
          />
        ) : (
          <div className="tscroll">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((member) => (
                  <tr className="hov" key={member.id}>
                    <td className="row" style={{ gap: 8 }}>
                      <span className="av">{initials(member.name, member.email)}</span>
                      <span className="fs13">
                        <b>{member.name ?? member.email}</b>
                      </span>
                    </td>
                    <td className="fs13 mono">{member.email}</td>
                    <td className="fs13">{member.role}</td>
                    <td>
                      <StatusPill tone={STATUS_TONE[member.status] ?? 'idle'} label={member.status} />
                    </td>
                    <td className="fs13 mono">
                      {member.lastActiveAt ? formatRelative(member.lastActiveAt) : 'pending'}
                    </td>
                    <td>
                      {member.id === user?.id ? (
                        <span className="fs12 faint">you</span>
                      ) : (
                        <button
                          className="btn btn-g btn-sm"
                          type="button"
                          onClick={() => void remove(member.id)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="card-f">
          <span className="fs12 faint">
            Narrow roles run jobs but cannot change billing, connect companies, or invite users.
          </span>
        </div>
      </div>

      <Modal
        open={inviteOpen}
        title="Invite a user"
        onClose={() => setInviteOpen(false)}
        footer={
          <>
            <button className="btn btn-g" type="button" onClick={() => setInviteOpen(false)}>
              Cancel
            </button>
            <span className="spacer" />
            <button
              className="btn btn-p"
              type="button"
              disabled={busy || !email.trim()}
              onClick={() => void invite()}
            >
              {busy ? 'Sending…' : 'Send invite'}
            </button>
          </>
        }
      >
        <div className="fgrp">
          <label className="fl" htmlFor="inviteEmail">
            Email <span className="req">*</span>
          </label>
          <input
            className="inp"
            id="inviteEmail"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="fgrp">
          <label className="fl" htmlFor="inviteName">
            Name
          </label>
          <input
            className="inp"
            id="inviteName"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="fgrp" style={{ margin: 0 }}>
          <label className="fl" htmlFor="inviteRole">
            Role
          </label>
          <select
            className="inp"
            id="inviteRole"
            value={role}
            onChange={(event) => setRole(event.target.value as Role)}
          >
            {ROLES.map((value) => (
              <option value={value} key={value}>
                {value}
              </option>
            ))}
          </select>
          <div className="hint">{ROLE_HELP[role]}</div>
        </div>
      </Modal>
    </>
  )

  return embedded ? body : <div className="wrap">{body}</div>
}
